import { CSP_NONCE, Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { OnFinish } from '../../api/api/on-finish';
import { environment } from 'src/environments/environment';
import { RokezzzWindowService } from 'src/app/rokezzz/window/services/window.service';
import { RokezzzPlatformService } from 'src/app/rokezzz/platform/services/platform.service';
import { first, timer } from 'rxjs';
import { RokezzzNotificationService } from '../../notification/services/notification.service';
import { RokezzzApiService } from '../../api/services/api.service';

declare var grecaptcha: any;

@Injectable({
    providedIn: 'root'
})
export class RokezzzRecaptchaService {
    private readonly recaptchaApiKey: string = environment.recaptchaApiKey;

    private recaptchaLoaded: boolean = false;
    private recaptchaLoading: boolean = false;
    private renderer: Renderer2;
    private document: Document;

    public constructor(
        @Inject(CSP_NONCE) private readonly cspNonce: string,
        private readonly rendererFactory: RendererFactory2,
        private readonly rokezzzNotificationService: RokezzzNotificationService,
        private readonly rokezzzApiService: RokezzzApiService,
        private readonly rokezzzWindowService: RokezzzWindowService,
        private readonly rokezzzPlatformService: RokezzzPlatformService,
    ) {
        this.renderer = rendererFactory.createRenderer(null, null);
        this.document = this.rokezzzWindowService.getDocument();
    }

    public prepareTokenForAction(action: string, onSuccess: OnFinish<void>): void {
        this.loadRecaptcha(() => {
            grecaptcha.ready(() => {
                grecaptcha.execute(this.recaptchaApiKey, { action: action })
                    .then((token: string) => {
                        this.rokezzzApiService.setRecaptchaToken(token);
                        onSuccess();
                    })
                    .catch(() => {
                        this.rokezzzNotificationService.addServerError();
                    });
            });
        });
    }

    private loadRecaptcha(onFinish: OnFinish<void>): void {
        if (this.rokezzzPlatformService.isPlatformServer()) {
            return;
        }

        if (this.recaptchaLoaded) {
            onFinish();

            return;
        }

        if (this.recaptchaLoading) {
            timer(1000).pipe(first()).subscribe(() => this.loadRecaptcha(onFinish));

            return;
        }

        this.recaptchaLoading = true;

        const scriptElement = this.renderer.createElement('script');

        this.renderer.setAttribute(scriptElement, 'src', `https://www.google.com/recaptcha/api.js?render=${this.recaptchaApiKey}`);
        scriptElement.defer = true;
        scriptElement.nonce = this.cspNonce;

        scriptElement.onload = () => {
            this.recaptchaLoaded = true;
            onFinish();
        };

        scriptElement.onerror = () => {
            this.recaptchaLoaded = false;
            this.rokezzzNotificationService.addServerError();
        };

        this.renderer.appendChild(this.document.body, scriptElement);
    }
}
